import { Button, Card, Grid, Loading, Spacer, Text } from "@nextui-org/react";
import { FlexBox } from "components/FlexBox";
import { NextImage } from "components/NextImage";
import { NextLink } from "components/NextLink";
import { useLeaderboardContents } from "hooks/useLeaderboardContents";
import { PageRoutes } from "lib/PageRoutes";
import type { NextPage } from "next";
import { useSession } from "next-auth/react";
import Head from "next/head";
import { useRouter } from "next/router";
import React, { useRef } from "react";
import { useInViewport } from "react-in-viewport";
import { CountUp } from "use-count-up";

const Home: NextPage = () => {
  return (
    <>
      <Head>
        <title>⚡SVRN Energy⚡</title>
      </Head>
      <Homepage />
    </>
  );
};

export default Home;

function HomepageCTA() {
  const { status: sessionStatus } = useSession();
  return (
    <NextLink
      href={
        sessionStatus === "authenticated"
          ? PageRoutes.dashboard
          : PageRoutes.signin
      }
      passHref
    >
      <a>
        <Button color="primary" size="lg">
          {sessionStatus === "authenticated" ? (
            <>Open dashboard</>
          ) : sessionStatus === "unauthenticated" ? (
            <>Claim Rewards &raquo;</>
          ) : (
            ""
          )}
        </Button>
      </a>
    </NextLink>
  );
}

function Homepage() {
  const { data: leaderboardContents } = useLeaderboardContents();
  const [pageLoaded, setPageLoaded] = React.useState(false);
  const { status: sessionStatus } = useSession();
  const router = useRouter();
  const { token } = router.query;

  React.useEffect(() => {
    const onPageLoad = () => {
      setPageLoaded(true);
    };

    if (document.readyState === "complete") {
      onPageLoad();
    } else {
      window.addEventListener("load", onPageLoad);
      return () => window.removeEventListener("load", onPageLoad);
    }
  }, []);
  React.useEffect(() => {
    if (sessionStatus === "authenticated") {
      router.push(PageRoutes.dashboard);
    }
  }, [router, sessionStatus]);

  React.useEffect(() => {
    if (token) {
      // breez jwt token
      router.replace(`${PageRoutes.verifySignin}/${token}`);
    }
  }, [router, token]);

  if (!router || !router.isReady || token || !pageLoaded) {
    return (
      <FlexBox style={{ height: "100%", justifyContent: "center" }}>
        <Loading />
      </FlexBox>
    );
  }

  return (
    <>
      <Spacer />
      <NextImage
        alt=""
        src="/images/SVRN_3_Cans_small.png"
        width={550}
        height={550}
      />
      <Spacer />
      <Text
        h1
        css={{
          textGradient: "45deg, #ff9400 -20%, #ffcf00 50%",
          lineHeight: 1.2,
          textAlign: "center",
        }}
      >
        Are you thirsty for sats?
      </Text>
      <Text h1 size="$4xl">
        Join the Bitcoin Revolution.
      </Text>
      <Spacer />
      <HomepageCTA />
      <Spacer y={5} />
      <Grid.Container sm={10} justify="center">
        <Card>
          <Card.Body>
            <Grid.Container gap={2}>
              <Grid
                sm={12}
                md={4}
                css={{
                  fg: 1,
                  jc: "flex-start",
                  ai: "center",
                  flexDirection: "column",
                  ta: "center",
                }}
              >
                <NextImage
                  alt=""
                  src="/images/gift.png"
                  width={128}
                  height={128}
                />
                <Text h3>Eat. Drink. Sats.</Text>
                <Text color="$gray700">
                  {
                    "If your tippee doesn't claim their tip, those precious sats return to you."
                  }
                </Text>
              </Grid>
              <Grid
                sm={12}
                md={4}
                css={{
                  jc: "flex-start",
                  ai: "center",
                  flexDirection: "column",
                  ta: "center",
                  fg: 1,
                }}
              >
                <NextImage
                  alt=""
                  src="/images/flag.png"
                  width={128}
                  height={128}
                />
                <Text h3>Progress tracker</Text>
                <Text color="$gray700">
                  Follow SVRN into the Bitcoin rabbit hole.
                </Text>
              </Grid>
              <Grid
                sm={12}
                md={4}
                css={{
                  jc: "flex-start",
                  ai: "center",
                  flexDirection: "column",
                  ta: "center",
                  fg: 1,
                }}
              >
                <NextImage
                  alt=""
                  src="/images/onboarding.png"
                  width={128}
                  height={128}
                />
                <Text h3>Onboarding you to Bitcoin</Text>
                <Text color="$gray700">
                  SVRN Energy will help onboard you to Bitcoin and install your
                  own wallet to withdraw Bitcoin.
                </Text>
              </Grid>
            </Grid.Container>
          </Card.Body>
        </Card>
      </Grid.Container>
      <Spacer y={5} />
      <Text
        h1
        size="$8xl"
        css={{
          textGradient: "45deg, #ff9400 -20%, #ffcf00 50%",
          lineHeight: 1.2,
          textAlign: "center",
        }}
      >
        Join the Bitcoin revolution.
      </Text>
      <HomepageCTA />
      <Spacer y={5} />
      <Text h3 style={{ textAlign: "center" }}>
        Learn More
      </Text>
      <Spacer />
      <NextLink href={PageRoutes.about} passHref>
        <a>
          <Button color="primary" size="lg">
            About
          </Button>
        </a>
      </NextLink>
      <Spacer y={4} />
    </>
  );
}

function TipCounter({ totalSatsSent }: { totalSatsSent: number }) {
  const myRef = useRef<HTMLElement>(null);
  const { inViewport } = useInViewport(myRef, {}, { disconnectOnLeave: false });

  return (
    <div style={{ textAlign: "center" }}>
      <Text h3>With SVRN Energy</Text>
      <Spacer y={0.5} />
      <Text
        b
        ref={myRef}
        css={{
          textGradient: "45deg, #ff9400 -20%, #ffcf00 50%",
          lineHeight: "$xs",
          mt: -10,
          fontSize: "$4xl",
          "@lg": {
            fontSize: "$8xl",
          },
        }}
      >
        {inViewport ? (
          <CountUp
            isCounting
            end={totalSatsSent}
            easing={"easeOutCubic"}
            duration={2}
            thousandsSeparator=","
          />
        ) : (
          0
        )}
        &nbsp;sats
      </Text>
      <Spacer />
      <Text h3>have been energized to date.</Text>
    </div>
  );
}
